import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import wordcloud from "highcharts/modules/wordcloud";
import exporting from "highcharts/modules/exporting";
import exportData from "highcharts/modules/export-data";
import fullscreen from "highcharts/modules/full-screen";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Box from "@mui/material/Box";
import ChartDatadrawer from "../ChartDatadrawer";
import GridLight from "highcharts/themes/grid-light";
import ChartFallbackImg from "./ChartFallbackImg";

// Initialize the Highcharts wordcloud module
GridLight(Highcharts);
wordcloud(Highcharts);
exporting(Highcharts);
exportData(Highcharts);
fullscreen(Highcharts);

const WordCloudDefaulter = ({ wordcloudData }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedPostOwner, setSelectedPostOwner] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      Highcharts.charts.forEach((chart) => {
        if (chart) chart.reflow();
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (
    !wordcloudData ||
    typeof wordcloudData !== "object" ||
    Array.isArray(wordcloudData) ||
    Object.keys(wordcloudData).length === 0
  ) {
    return <ChartFallbackImg titleText="Repeat Offenders" />;
  }

  // Convert data to array and sort by weight
  const sortedData = Object.entries(wordcloudData)
    .map(([key, value]) => ({
      name: key,
      weight: value,
    }))
    .sort((a, b) => b.weight - a.weight);

  // Calculate dynamic threshold based on data distribution
  const calculateDynamicThreshold = (data) => {
    if (data.length <= 40) return 1;

    const weights = data.map((item) => item.weight);
    const mean = weights.reduce((sum, val) => sum + val, 0) / weights.length;
    const stdDev = Math.sqrt(
      weights.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
        weights.length
    );

    if (data.length > 100) {
      return mean - 0.3 * stdDev;
    } else {
      return Math.max(3, mean - stdDev);
    }
  };

  const calculateOptimalCount = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const viewportArea = viewportWidth * viewportHeight;
    const baseCount = Math.floor(viewportArea / 12000);
    return Math.min(Math.max(20, baseCount), 100);
  };

  const threshold = calculateDynamicThreshold(sortedData);
  const optimalCount = calculateOptimalCount();

  // Transform and filter data with new color scheme
  const transformedData = sortedData
    .filter((item) => item.weight >= threshold)
    .slice(0, optimalCount)
    .map((item, index) => {
      const colorScheme = [
        "#264653", // Deep Space Blue
        "#287271", // Deep Teal
        "#2A9D8F", // Ocean Green
        "#8AB17D", // Sage Green
        "#E9C46A", // Warm Sand
        "#F4A261", // Peach
        "#EE8959", // Coral
        "#E76F51", // Terra Cotta
        "#BA3B46", // Wine Red
        "#832232", // Burgundy
        "#55828B", // Steel Blue
        "#3B6064", // Dark Teal
        "#687864", // Forest Green
        "#9C6644", // Rust Brown
        "#694E4E", // Mocha
      ];

      return {
        ...item,
        color: colorScheme[index % colorScheme.length],
      };
    });

  const handleWordClick = (event) => {
    const postOwner = event.point.name;
    setSelectedPostOwner(postOwner);
    setIsDrawerOpen(true);
  };

  const options = {
    chart: {
      type: "wordcloud",
      backgroundColor: "#ffffff",
      events: {
        load: function () {
          this.reflow();
        },
      },
    },
    credits: {
      enabled: false,
    },
    title: {
      text: `Top Users by Activity (${transformedData.length} most active)`,
    },
    tooltip: {
      pointFormat:
        '<span style="color:{point.color}">●</span> <h1>{point.weight}</h1><br/>',
      style: {
        fontSize: "20px",
        fontWeight: "bold",
      },
    },
    plotOptions: {
      series: {
        cursor: "pointer",
        point: {
          events: {
            click: handleWordClick,
          },
        },
      },
      wordcloud: {
        minFontSize: 20,
        maxFontSize: 52,
        rotation: {
          from: -20,
          to: 20,
          orientations: 3,
        },
      },
    },
    series: [
      {
        type: "wordcloud",
        data: transformedData,
        rotation: {
          from: 0,
          to: 0,
        },
        colorByPoint: true,
      },
    ],
    exporting: {
      buttons: {
        contextButton: {
          menuItems: [
            "viewData",
            "viewFullscreen",
            "printChart",
            "separator",
            "downloadPNG",
            "downloadJPEG",
            "downloadPDF",
            "downloadSVG",
          ],
        },
      },
      csv: {
        columnHeaderFormatter: (item, key) => {
          if (!item) {
            return key === "name" ? "Keyword" : "Count";
          }
          return false;
        },
      },
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 600,
          },
          chartOptions: {
            title: {
              style: {
                fontSize: "16px",
              },
            },
            series: [
              {
                dataLabels: {
                  style: {
                    fontSize: "10px",
                  },
                },
              },
            ],
          },
        },
      ],
    },
  };

  return (
    <>
      <div style={{ width: "100%", maxWidth: "1200px", margin: "0 auto" }}>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
      <SwipeableDrawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onOpen={() => setIsDrawerOpen(true)} // 🔧 Add this line
        PaperProps={{
          sx: {
            height: "100%",
            overflow: "hidden",
            borderTopLeftRadius: "12px",
            borderBottomLeftRadius: "12px",
          },
        }}
      >
        <Box
          sx={{
            width: 650,
            padding: 2,
            height: "100%",
            overflow: "hidden",
          }}
        >
          {selectedPostOwner && (
            <ChartDatadrawer postOwner={selectedPostOwner} />
          )}
        </Box>
      </SwipeableDrawer>
    </>
  );
};

export default WordCloudDefaulter;
